import {TimeEntryEditCard} from './TimeEntryEditCard.tsx'
import type {DayItem, TimeEntry} from '@package/shared'
import {Skeleton, Text, Title} from '@mantine/core'
import {Fragment, useMemo, useState} from 'react'
import {formatLocalDate} from './formatter/formatLocalDate.ts'
import dayjs from 'dayjs'
import {RecommendationEditCard} from './RecommendationEditCard.tsx'
import {InsertTimeEntryButton} from './components/InsertTimeEntryButton.tsx'
import {mapWorkdayDateRange} from './mapper/mapWorkdayDateRange.ts'
import {useFilters} from './providers/filters/useFilters.ts'
import {range} from 'lodash'
import {useLocalSettings} from '../../providers/localSettings/useLocalSettings.ts'
import {useProject} from '../../providers/projects/useProject.ts'
import type {DateRange} from '../../components/dateRangeSelector/DateRangeSelector.tsx'
import type {AcceptRecommendationRequest, RejectRecommendationRequest} from '@app/api-resolver/router'
import {useUserSettings} from '../../providers/userSettings/useUserSettings.ts'
import {mapDayHours} from './mapper/mapDayHours.ts'
import {formatHours} from './formatter/formatHours.ts'

type Props = {
  day: DateRange
  dayItems: DayItem[]
  onAcceptRecommendation: (recommendation: AcceptRecommendationRequest) => Promise<TimeEntry>
  onRejectRecommendation: (recommendation: RejectRecommendationRequest) => Promise<void>
  loading: boolean
  disabled: boolean
}

export const DayColumn = ({
  day,
  dayItems,
  onAcceptRecommendation,
  onRejectRecommendation,
  loading,
  disabled,
}: Props) => {
  const {userSettings} = useUserSettings()
  const settings = useLocalSettings()
  const {projectId} = useFilters()
  const project = useProject(projectId)
  const [editModeIds, setEditModeIds] = useState<string[]>([])

  const sortedDayItems = useMemo(() => {
    return dayItems.toSorted((a, b) => dayjs(a.item.start).unix() - dayjs(b.item.start).unix())
  }, [dayItems])

  const workday = project ? mapWorkdayDateRange(day.start, project.workdays) : undefined
  const firstItem = sortedDayItems.at(0)?.item
  const lastItem = sortedDayItems.at(-1)?.item
  const firstStart = firstItem ? dayjs(firstItem.start) : undefined
  const lastEnd = lastItem ? dayjs(lastItem.end) : undefined

  const totalHours = mapDayHours(sortedDayItems.filter((item) => item.type === 'time-entry').map((item) => item.item))

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: 5}}>
      <div style={{textAlign: 'center'}}>
        <Title order={5}>{formatLocalDate(day.start, userSettings, true)}</Title>
        <Text size={'sm'} c={'dimmed'}>
          {formatHours(totalHours)}
        </Text>
      </div>
      {!loading && workday && (
        <InsertTimeEntryButton
          day={day}
          projectId={projectId}
          start={(firstStart || workday.start).subtract(1, 'hour')}
          end={firstStart || workday.start}
          customLabel={'Insert'}
          disabled={disabled}
        />
      )}
      {!loading && (
        <InsertTimeEntryButton
          day={day}
          projectId={projectId}
          start={workday?.start}
          end={firstStart ? firstStart : workday?.end}
          disabled={disabled}
        />
      )}

      {loading && range(0, 5).map((i) => <Skeleton key={i} h={settings.compactMode ? 40 : 100} />)}

      {sortedDayItems.map((dayItem, index, timeEntries) => {
        const nextDayItem = timeEntries[index + 1]?.item

        return (
          <Fragment key={dayItem.item.id}>
            <div>
              {dayItem.type === 'time-entry' && (
                <TimeEntryEditCard
                  day={day}
                  entry={dayItem.item}
                  onEditModeEntered={({id}) => setEditModeIds((editModeIds) => [...editModeIds, id])}
                  onEditModeLeft={({id}) =>
                    setEditModeIds((editModeIds) => editModeIds.filter((editModeId) => editModeId !== id))
                  }
                  editMode={editModeIds.includes(dayItem.item.id)}
                  disabled={disabled}
                />
              )}
              {(dayItem.type === 'recommendation' || dayItem.type === 'merged-recommendation') && (
                <RecommendationEditCard
                  day={day}
                  recommendation={dayItem.item}
                  onAccept={onAcceptRecommendation}
                  onReject={onRejectRecommendation}
                  disabled={disabled}
                />
              )}
            </div>
            {nextDayItem && (
              <InsertTimeEntryButton
                day={day}
                projectId={projectId}
                start={dayjs(dayItem.item.end)}
                end={dayjs(nextDayItem.start)}
                disabled={disabled}
              />
            )}
          </Fragment>
        )
      })}

      {sortedDayItems.length > 0 && (
        <InsertTimeEntryButton
          day={day}
          projectId={projectId}
          start={lastEnd ? lastEnd : workday?.start}
          end={workday?.end}
          disabled={disabled}
        />
      )}

      {!loading && workday && (
        <InsertTimeEntryButton
          day={day}
          projectId={projectId}
          start={lastEnd || workday.end}
          end={(lastEnd || workday.end).add(1, 'hour')}
          customLabel={'Insert'}
          disabled={disabled}
        />
      )}
    </div>
  )
}
